import CommonTable, { DataNode, HeaderCells } from "@/components/common/CommonTable";
import CustomerDetailsHeaderActions from "@/components/customer-page-components/customer-details/CustomerDetailsHeaderActions";
import AddFilterButton from "@/components/customer-page-components/filters/AddFilterButton";
import CreateLazoTeamMemberModal from "@/components/lazo-team-page-components/CreateLazoTeamMemberModal";
import PageHead from "@/components/seo/PageHead";
import CommonPagination from "@/components/utils/CommonPagination";
import UserImage from "@/components/utils/UserImage";
import { useAppBarContent } from "@/contexts/AppBarContentContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import { LazoTeamMemberRoles } from "@/enums/LazoTeamMemberRoles";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { SmallLazoTeamUser } from "@/models/SmallLazoTeamUser";
import LazoTeamService from "@/services/LazoTeamService";
import { LazoArea } from "@/types/LazoArea";
import { LazoJobTitle } from "@/types/LazoJobTitle";
import { LazoTeamPageFilters } from "@/types/LazoTeamPageFilters";
import { LAZO_USER_ROLES, mapRolesToString } from "@/types/LazoUserRoles";
import { FilterCategoryType, getCategoryFilters, hasSelectedFilters } from "@/utils/FiltersUtils";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { Box, IconButton, Tooltip, Typography, styled, tableCellClasses } from "@mui/material";
import { RefObject, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActiveIndicatorSVG from "../../assets/images/active-indicator-green.svg";
import InactiveIndicatorSVG from "../../assets/images/inactive-indicator-orange.svg";

const PARENT_NODE: DataNode = {
  checked: false,
  externalId: "Top Node",
  children: [],
  cells: [],
};

export default function LazoTeamPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DataNode>(PARENT_NODE);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [selectedFilters, setSelectedFilters] = useState<FilterCategoryType<null>[]>([]);
  const [addedFilterCategories, setAddedFilterCategories] = useState<FilterCategoryType<null>[]>([]);
  const [areas, setAreas] = useState<LazoArea[]>([]);
  const [jobTitles, setJobTitles] = useState<LazoJobTitle[]>([]);

  const { workspaceState } = useTenantMemberships();

  const isAppAdmin = AuthService.hasPermissionFor("App:Administration");
  const isTeamAdmin = workspaceState.lazoMembership?.roles.some((r) => r.name === LAZO_USER_ROLES.LAZO_TEAM_ADMIN);
  const canEdit = isAppAdmin || isTeamAdmin;

  useAppBarContent("Lazo Team - Back office");

  function handleSearch(searchRef: RefObject<HTMLInputElement>) {
    setSearch(searchRef.current.value.trim());
  }

  useEffect(() => {
    fetchLazoTeam(true, 0);
    LazoTeamService.getAllAreas().then((r) => r.onSuccess(setAreas));
    LazoTeamService.getJobTitles().then((r) => r.onSuccess(setJobTitles));
  }, []);

  useEffect(() => {
    if (hasSelectedFilters(selectedFilters) || search) {
      fetchFiltered(page, search);
    } else {
      fetchLazoTeam(false, page);
    }
  }, [selectedFilters, search, page]);

  function fetchLazoTeam(updateLoading: boolean, page: number) {
    updateLoading && setLoading(true);
    LazoTeamService.getLazoTeamPaged(page, 20, DEFAULT_LAZO_TEAM_FILTERS)
      .then((r) => {
        r.onSuccess((page) => {
          // @ts-ignore
          setTotalPages(page.page.totalPages);

          mapData(page.content);
        });
      })
      .finally(() => {
        updateLoading && setLoading(false);
      });
  }

  function handlePageChange(_, page: number) {
    setPage(page - 1);
  }

  function fetchFiltered(page: number, search?: string) {
    let filters: LazoTeamPageFilters;
    filters = {
      roles: getCategoryFilters<LazoTeamMemberRoles>("roles", selectedFilters),
      jobTitle: getCategoryFilters<string>("jobTitle", selectedFilters),
      area: getCategoryFilters<string>("area", selectedFilters),
      status: getCategoryFilters<STATUS>("status", selectedFilters),
      search: search || null,
    };
    setLoading(true);
    LazoTeamService.getLazoTeamPaged(0, 20, filters)
      .then((r) => {
        r.onSuccess((page) => {
          mapData(page.content);
          // @ts-ignore
          setTotalPages(page.page.totalPages);

          if (page.content.length === 0) {
            NotificationService.info("Users with provided filters not found.");
          }
        });
        r.onError((c) => {});
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function mapData(lazoTeamUsers: SmallLazoTeamUser[]) {
    const initializedData: DataNode = {
      ...data,
      children: lazoTeamUsers.map((lazoTeamUser) => ({
        externalId: lazoTeamUser.externalId!,
        cells: buildCells(lazoTeamUser),
        checked: false,
        children: [],
      })),
    };
    setData(initializedData);
  }

  function buildCells(lazoEmployee: SmallLazoTeamUser) {
    return [
      <CellContent key={lazoEmployee.externalId + "-full-name"} className="full-name-container">
        <UserImage src={lazoEmployee.imageUrl} />
        <Typography fontWeight={600} fontSize={"0.875rem"}>
          {lazoEmployee.firstName + " " + lazoEmployee.lastName}
        </Typography>
      </CellContent>,
      <CellContent key={lazoEmployee.externalId + "-email"}>
        <Typography fontWeight={600} fontSize={"0.875rem"}>
          {lazoEmployee?.email || "-"}
        </Typography>
      </CellContent>,
      <CellContent key={lazoEmployee.externalId + "-job-title"}>
        <Typography fontWeight={600} fontSize={"0.875rem"}>
          {lazoEmployee.jobTitle || "-"}
        </Typography>
      </CellContent>,
      <CellContent key={lazoEmployee.externalId + "-area"}>
        <Typography fontWeight={600} fontSize={"0.875rem"}>
          {lazoEmployee.area || "-"}
        </Typography>
      </CellContent>,
      <CellContent key={lazoEmployee.externalId + "-role"}>
        <Typography fontWeight={600} fontSize={"0.875rem"}>
          {lazoEmployee.roles.map((r, idx) => {
            return idx + 1 == lazoEmployee.roles.length ? `${r}` : `${r}, `;
          })}
        </Typography>
      </CellContent>,
      <CellContent key={lazoEmployee.externalId + "-status"} className="status-container">
        <Typography fontWeight={600} fontSize={"0.875rem"}>
          {lazoEmployee.status}
        </Typography>
        <img alt="status-icon" src={lazoEmployee.status == "Active" ? ActiveIndicatorSVG.src : InactiveIndicatorSVG.src} />
      </CellContent>,
      <CellContent key={lazoEmployee.externalId + "-actions"} sx={{ display: "grid", placeItems: "center" }}>
        <Tooltip title="Profile">
          <IconButton
            sx={{ color: Colors.BUTTON_PURPLE }}
            onClick={() => {
              navigate(LazoPaths.LAZO_TEAM_MEMBER_DETAILS.replace(":externalId", lazoEmployee?.externalId));
            }}
          >
            <PersonOutlineOutlinedIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
        </Tooltip>
      </CellContent>,
    ];
  }

  return (
    <Container className="root-container">
      <PageHead namespace="settings" titleKey="lazoTeam.meta.title" descriptionKey="lazoTeam.meta.description" />
      <Container className="header">
        <Typography>Lazo Team - Back office</Typography>
      </Container>

      <Body>
        <ActionsContainer>
          <CustomerDetailsHeaderActions
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            handleSearch={handleSearch}
            handleUpdateInvitationData={() => {}}
            setRefetchData={() => {}}
            tenantExternalId={workspaceState.lazoMembership?.tenant.externalId!}
            hideInvite
          />
          {canEdit && (
            <CreateLazoTeamMemberModal
              onCreateUser={() => {
                fetchLazoTeam(false, page);
              }}
            />
          )}
        </ActionsContainer>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <CommonPagination onChange={handlePageChange} page={page + 1} count={totalPages} />
        </Box>
        {showFilters && (
          <TableFilters>
            <AddFilterButton
              filterCategories={buildFilterCategories(areas, jobTitles)}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              setAddedFilterCategories={setAddedFilterCategories}
              addedFilterCategories={addedFilterCategories}
            />
          </TableFilters>
        )}

        <TableWrapper>
          <CommonTable loading={loading} headerCells={headerCells} setData={setData} data={data} actions={[]} hideCheckboxes />
        </TableWrapper>
      </Body>
    </Container>
  );
}
const Container = styled(Box)(({ theme }) => ({
  "&.root-container": {
    padding: "1rem",
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
  },
  "&.header": {
    "& p": {
      fontSize: "1.5rem",
      fontWeight: "500",
    },
    paddingBottom: "1rem",

    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
}));

const TableFilters = styled(Box)(() => ({
  display: "flex",
  gap: "1.000rem",
  backgroundColor: "white !important",
}));

const ActionsContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  width: "100%",
  "& > div:first-of-type": {
    flex: 1,
  },
}));

const CellContent = styled(Box)(() => ({
  paddingInline: "12px",
  "&.full-name-container": {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  "&.status-container": {
    display: "flex",
    justifyContent: "space-between",
    width: "9.375rem",
  },
}));

const Body = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  padding: "1rem",
  gap: "1rem",
  backgroundColor: "white",
  borderRadius: "1rem",
  boxShadow: "0 0 1.688rem rgba(0, 0, 0, 0.07)",
}));

const TableWrapper = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "0.75rem",
  overflow: "hidden",
  "& thead": {
    [`& .${tableCellClasses.root}`]: {
      padding: "0 0 0 20px",
      borderTop: "0 !important",
    },
  },
  "& thead tr th:first-of-type": {
    borderLeft: "0 !important",
  },
  "& tbody tr td:first-of-type": {
    borderLeft: "0 !important",
  },
  "& thead tr th:last-of-type": {
    borderRight: "0 !important",
  },
  "& tbody tr td:last-of-type": {
    borderRight: "0 !important",
  },
  "& tbody tr:last-of-type td": {
    borderBottom: "0 !important",
  },
}));

export const DEFAULT_LAZO_TEAM_FILTERS: LazoTeamPageFilters = {
  roles: [],
  jobTitle: [],
  area: [],
  status: null,
  search: "",
};

const headerCells: HeaderCells[] = [
  {
    id: "full-name",
    label: "Full Name",
  },
  {
    id: "email",
    label: "Email",
  },
  {
    id: "job-title",
    label: "Job Title",
  },
  {
    id: "area",
    label: "Area",
  },
  {
    id: "role",
    label: "Role",
  },
  {
    id: "status",
    label: "Status",
  },
  {
    id: "actions",
    label: "Actions",
    sticky: true,
  },
];

enum STATUS {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

function buildFilterCategories(areas: LazoArea[], jobTitles: LazoJobTitle[]): FilterCategoryType<unknown>[] {
  return [
    {
      id: "roles",
      render: "Roles",
      filters: [LazoTeamMemberRoles.LAZO_TEAM_ADMIN, LazoTeamMemberRoles.LAZO_USER].sort((a, b) => a.localeCompare(b)),
      filtersRenders: [LazoTeamMemberRoles.LAZO_TEAM_ADMIN, LazoTeamMemberRoles.LAZO_USER]
        .sort((a, b) => a.localeCompare(b))
        .map((r) => mapRolesToString(r)),
    },
    {
      id: "jobTitle",
      render: "Job Title",
      filters: jobTitles.map((jt) => jt.name).sort((a, b) => a.localeCompare(b)),
    },
    {
      id: "area",
      render: "Area",
      filters: areas.map((a) => a.name).sort((a, b) => a.localeCompare(b)),
    },
    {
      id: "status",
      render: "Status",
      filters: [...Object.keys(STATUS).sort((a, b) => a.localeCompare(b))],
    },
  ];
}
